import { useCallback, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  CANCEL_PAYMENT_LINK_SUBSCRIPTION,
  REVOKE_PAYMENT_LINK_SUBSCRIPTION_ACCESS,
  GET_PAYMENT_LINK_SUBSCRIPTION,
  GET_SUBSCRIPTION_CHARGES,
} from "graphql/subscriptions";
import {
  DeclineClass,
  SubscriptionCanceledBy,
  SubscriptionChargeStatus,
  SubscriptionStatus,
} from "enums/Subscription";
import { BillingInterval } from "enums/BillingInterval";

export interface PaymentLinkSubscription {
  id: string;
  paymentLinkId?: string;
  paymentLink?: { id: string; name: string; url?: string };
  contact?: {
    id: string;
    firstName?: string;
    lastName?: string | null;
    businessName?: string;
    email?: string;
  };
  payerEmail: string;
  payerName?: string;
  baseAmount: number;
  feeCoverElected?: boolean;
  feeCoverAmount?: number;
  chargeAmount: number;
  currency: string;
  status: SubscriptionStatus;
  interval: BillingInterval;
  anchorDay?: number;
  nextBillingDate?: number;
  currentPeriod?: string;
  lastBilledDate?: number;
  failedAttempts?: number;
  lastDeclineCode?: string;
  lastDeclineClass?: DeclineClass;
  awaitingPaymentMethod?: boolean;
  cancelAtPeriodEnd?: boolean;
  canceledAt?: number;
  cancelReason?: string;
  canceledBy?: SubscriptionCanceledBy;
  consentAt?: number;
  mandateTextSnapshot?: string;
  paymentMethodSummary?: {
    methodType?: string;
    brand?: string;
    last4?: string;
  };
  createdAt?: number;
}

export interface SubscriptionCharge {
  id: string;
  period: string;
  attempt: number;
  amount: number;
  currency: string;
  status: SubscriptionChargeStatus;
  declineCode?: string;
  declineClass?: DeclineClass;
  transactionId?: string;
  createdAt?: number;
}

export function useSubscriptionData() {
  const { t } = useTranslation();
  const [subscription, setSubscription] =
    useState<PaymentLinkSubscription | null>(null);
  const [charges, setCharges] = useState<SubscriptionCharge[]>([]);

  const [fetchSubscription, { loading: loadingSubscription }] = useLazyQuery(
    GET_PAYMENT_LINK_SUBSCRIPTION,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) =>
        setSubscription(data?.paymentLinkSubscription || null),
      onError: () => toast.error(t("Could not load this subscription")),
    },
  );

  const [fetchCharges, { loading: loadingCharges }] = useLazyQuery(
    GET_SUBSCRIPTION_CHARGES,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => setCharges(data?.subscriptionCharges || []),
    },
  );

  const [cancelSubscription, { loading: canceling }] = useMutation(
    CANCEL_PAYMENT_LINK_SUBSCRIPTION,
  );

  const [revokeAccess, { loading: revoking }] = useMutation(
    REVOKE_PAYMENT_LINK_SUBSCRIPTION_ACCESS,
  );

  const loadSubscription = useCallback(
    (id: string) => {
      fetchSubscription({ variables: { id } });
      fetchCharges({ variables: { subscriptionId: id } });
    },
    [fetchSubscription, fetchCharges],
  );

  const cancel = useCallback(
    async (id: string, atPeriodEnd: boolean, reason?: string) => {
      try {
        await cancelSubscription({ variables: { id, atPeriodEnd, reason } });
        toast.success(
          atPeriodEnd
            ? t("Subscription will end after the paid period")
            : t("Subscription canceled"),
        );
        return true;
      } catch (error) {
        toast.error(
          messageOf(error) || t("Could not cancel this subscription"),
        );
        return false;
      }
    },
    [cancelSubscription, t],
  );

  const revokeManageLinks = useCallback(
    async (id: string) => {
      try {
        await revokeAccess({ variables: { id } });
        toast.success(t("Manage links revoked"));
        return true;
      } catch (error) {
        toast.error(messageOf(error) || t("Could not revoke these links"));
        return false;
      }
    },
    [revokeAccess, t],
  );

  return {
    subscription,
    charges,
    loadSubscription,
    cancel,
    revokeManageLinks,
    loading: loadingSubscription || loadingCharges || canceling || revoking,
  };
}

function messageOf(error: unknown): string | undefined {
  return (error as { message?: string })?.message;
}
